import { Component, computed, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  LucideAngularModule,
  LayoutDashboard,
  Package,
  ChartBar,
  PackageMinus,
  ShoppingBag,
} from 'lucide-angular';
import { AuthService } from '../../core/services/auth.service';
import { hasAnyPermission } from '../../core/utils/jwt.util';

interface BottomNavItem {
  translateKey: string;
  href: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  permissions: string[];
  exact: boolean;
}

const NAV_ITEMS: BottomNavItem[] = [
  {
    translateKey: 'SIDEBAR.DASHBOARD',
    href: '/app',
    icon: LayoutDashboard,
    permissions: ['DASHBOARD_VIEW'],
    exact: true,
  },
  {
    translateKey: 'SIDEBAR.PRODUCTS',
    href: '/app/products',
    icon: Package,
    permissions: ['PRODUCT_VIEW'],
    exact: false,
  },
  {
    translateKey: 'SIDEBAR.SALES',
    href: '/app/sales',
    icon: ShoppingBag,
    permissions: ['ORDER_CREATE'],
    exact: false,
  },
  {
    translateKey: 'SIDEBAR.ANALYTICS',
    href: '/app/analytics',
    icon: ChartBar,
    permissions: ['INVENTORY_VIEW'],
    exact: false,
  },
  {
    translateKey: 'SIDEBAR.WRITEOFF',
    href: '/app/write-off',
    icon: PackageMinus,
    permissions: ['INVENTORY_WRITEOFF'],
    exact: false,
  },
];

const SYSTEM_STAFF_ROLES = ['SuperAdmin', 'Admin', 'Operator'];

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [NgClass, RouterModule, TranslatePipe, LucideAngularModule],
  template: `
    @if (navItems().length) {
      <nav
        class="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-800"
        style="padding-bottom: env(safe-area-inset-bottom)"
      >
        <div class="flex h-16">
          @for (item of navItems(); track item.href) {
            <a
              [routerLink]="item.href"
              routerLinkActive
              [routerLinkActiveOptions]="{ exact: item.exact }"
              #rla="routerLinkActive"
              class="flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors duration-200 relative"
              [ngClass]="
                rla.isActive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              <!-- Top indicator bar -->
              <span
                class="absolute top-0 w-8 h-[2.5px] rounded-b-full transition-all duration-300"
                [ngClass]="rla.isActive ? 'bg-green-500' : 'bg-transparent'"
              ></span>

              <!-- Icon bubble -->
              <div
                class="flex items-center justify-center w-9 h-7 rounded-xl transition-all duration-200"
                [ngClass]="rla.isActive ? 'bg-green-50 dark:bg-green-950/50' : ''"
              >
                <lucide-icon [img]="item.icon" class="w-5 h-5 block shrink-0"></lucide-icon>
              </div>

              <!-- Label -->
              <span class="text-[10px] font-medium leading-none tracking-wide">
                {{ item.translateKey | translate }}
              </span>
            </a>
          }
        </div>
      </nav>
    }
  `,
})
export class BottomNavComponent {
  private authService = inject(AuthService);

  navItems = computed(() => {
    const role = this.authService.user()?.role;
    const token = this.authService.getAccessToken();
    const accountPermissions = this.authService.user()?.permissions;

    if (role && SYSTEM_STAFF_ROLES.includes(role)) return [];

    return NAV_ITEMS.filter((item) =>
      hasAnyPermission(token, role, item.permissions, accountPermissions),
    );
  });
}
